import React, { useState, useEffect } from 'react';
import Card from "components/card";
import { 
  MdSearch, 
  MdEdit, 
  MdDelete, 
  MdError, 
  MdFilterList, 
  MdCalendarToday, 
  MdCategory,
  MdInventory,
  MdLocationOn,
  MdClose,
  MdSave,
  MdGrade,
  MdNumbers,
  MdArrowBack,
  MdWarning
} from "react-icons/md";
import axios from 'axios';
import { toast, Toaster } from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';

const Loader = () => (
  <div className="flex h-[400px] items-center justify-center">
    <div className="relative">
      <div className="h-24 w-24 rounded-full border-t-4 border-b-4 border-brand-500"></div>
      <div className="absolute top-0 left-0 h-24 w-24 animate-spin rounded-full border-t-4 border-b-4 border-gray-200"></div>
      <div className="absolute top-1/2 left-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand-500"></div>
      <div className="mt-8 text-center">
        <p className="text-lg font-medium text-gray-600 dark:text-white">Loading</p>
        <p className="text-sm text-gray-400">Please wait...</p>
      </div>
    </div>
  </div>
);

const ProductTypeDetail = () => {
  const { productType } = useParams();
  const navigate = useNavigate();
  const [batches, setBatches] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    quantity: ''
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [batchToDelete, setBatchToDelete] = useState(null);
  const [deleteNote, setDeleteNote] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [batchToEdit, setBatchToEdit] = useState(null);
  const [editForm, setEditForm] = useState({
    date: '',
    quantity: '',
    lane: '',
    slot: ''
  });
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const itemsPerPage = 10;

  // Check user role
  useEffect(() => {
    try {
      const token = localStorage.getItem('usibras_login');
      if (token) {
        const decodedToken = jwtDecode(token);
        setUserRole(decodedToken.role);
      }
    } catch (error) {
      console.error('Error checking user role:', error);
      setUserRole(null);
    }
  }, []);

  // Fetch data from API
  useEffect(() => {
    const fetchBatches = async () => {
      try {
        setLoading(true);
        const response = await axios.get('https://usibras-web-backend.onrender.com/api/fetch-finished-products');
        if (response.data && response.data.data) {
          // Find the product type that matches the current productType
          const productData = response.data.data.find(
            product => product.product_type === decodeURIComponent(productType)
          );
          
          if (productData && productData.batch_details) {
            setBatches(productData.batch_details);
          } else {
            setBatches([]);
          }
        } else {
          setBatches([]);
        }
      } catch (err) {
        console.error('Error:', err);
        setError('Error loading batches');
        setBatches([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchBatches();
  }, [productType]);

  // Apply filters and search
  const filteredData = batches.filter(item => {
    const searchTermLower = searchTerm.toLowerCase();
    const searchableValues = [
      item.batch_id,
      item.date,
      item.rcn_type,
      item.batch_actual_stock?.toString(),
      `${item.lane || `Lane ${item.zone}`}, Slot ${item.slot || item.rack}`
    ].filter(Boolean);

    const matchesSearch = searchTermLower === '' || 
      searchableValues.some(value => 
        value.toLowerCase().includes(searchTermLower)
      );

    const matchesDate = (!filters.startDate || new Date(item.date) >= new Date(filters.startDate)) &&
                       (!filters.endDate || new Date(item.date) <= new Date(filters.endDate));
    
    const matchesQuantity = !filters.quantity || item.batch_actual_stock.toString() === filters.quantity;

    return matchesSearch && matchesDate && matchesQuantity;
  });

  // Calculate summary statistics
  const totalQuantity = filteredData.reduce((sum, item) => sum + (parseInt(item.batch_actual_stock) || 0), 0);

  // Pagination
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const paginatedData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const TABLE_HEAD = ["Batch Number", "Date", "Julian Date", "RCN Type", "Quantity", "Location", "Actions"];

  // Add handleEdit function
  const handleEdit = (batch) => {
    openEditModal(batch);
  };

  // Add handleDelete function
  const handleDelete = async (batchId, note = '') => {
    setIsDeleting(true);
    try {
      const response = await axios.delete(`https://usibras-web-backend.onrender.com/api/finished-products/${batchId}`, {
        data: { note: note }
      });
      
      if (response.data && response.data.message) {
        // Show success message with batch details
        const deletedProduct = response.data.deleted_product;
        if (deletedProduct) {
          toast.success(
            `Batch ${deletedProduct.batch_id} (${deletedProduct.quantity} boxes) moved to deleted products successfully.`,
            {
              duration: 5000,
              style: {
                background: '#10B981',
                color: '#FFFFFF',
                fontSize: '14px',
                padding: '16px',
                borderRadius: '8px',
              },
            }
          );
        } else {
          toast.success(response.data.message, {
            duration: 4000,
            style: {
              background: '#10B981',
              color: '#FFFFFF',
              fontSize: '14px',
              padding: '16px',
              borderRadius: '8px',
            },
          });
        }
        
        // Remove the deleted batch from the local state
        setBatches(prevBatches => prevBatches.filter(batch => batch.batch_id !== batchId));
      } else {
        toast.error('Failed to delete batch. Please try again.');
      }
    } catch (error) {
      console.error('Error deleting batch:', error);
      
      // Show more specific error messages
      if (error.response) {
        const errorMessage = error.response.data?.message || error.response.data?.error || 'Failed to delete batch';
        toast.error(errorMessage, {
          duration: 5000,
          style: {
            background: '#EF4444',
            color: '#FFFFFF',
            fontSize: '14px',
            padding: '16px',
            borderRadius: '8px',
          },
        });
      } else if (error.request) {
        toast.error('Network error. Please check your connection and try again.', {
          duration: 5000,
          style: {
            background: '#EF4444',
            color: '#FFFFFF',
            fontSize: '14px',
            padding: '16px',
            borderRadius: '8px',
          },
        });
      } else {
        toast.error('Failed to delete batch. Please try again.', {
          duration: 4000,
          style: {
            background: '#EF4444',
            color: '#FFFFFF',
            fontSize: '14px',
            padding: '16px',
            borderRadius: '8px',
          },
        });
      }
    } finally {
      setIsDeleting(false);
    }
  };

  // Add confirmation dialog functions
  const openDeleteConfirm = (batch) => {
    setBatchToDelete(batch);
    setShowDeleteConfirm(true);
  };

  const closeDeleteConfirm = () => {
    setShowDeleteConfirm(false);
    setBatchToDelete(null);
    setDeleteNote('');
    setIsDeleting(false);
  };

  const confirmDelete = async () => {
    if (batchToDelete && deleteNote.trim()) {
      await handleDelete(batchToDelete.batch_id, deleteNote);
      closeDeleteConfirm();
    }
  };

  // Add edit modal functions
  const openEditModal = (batch) => {
    setBatchToEdit(batch);
    setEditForm({
      date: batch.date ? new Date(batch.date).toISOString().split('T')[0] : '',
      quantity: batch.batch_actual_stock || '',
      lane: batch.lane || batch.zone || '',
      slot: batch.slot || batch.rack || ''
    });
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setBatchToEdit(null);
    setEditForm({
      date: '',
      quantity: '',
      lane: '',
      slot: ''
    });
    setIsEditing(false);
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!batchToEdit) return;

    setIsEditing(true);
    try {
      const response = await axios.put(`https://usibras-web-backend.onrender.com/api/finished-products/${batchToEdit.batch_id}`, {
        product_type: batchToEdit.product_type,
        date: editForm.date,
        quantity: parseInt(editForm.quantity),
        lane: editForm.lane,
        slot: editForm.slot
      });

      if (response.data && response.data.message === "Product updated successfully") {
        toast.success('Batch updated successfully!');
        
        // Update the local state with the new data
        setBatches(prevBatches => 
          prevBatches.map(batch => 
            batch.batch_id === batchToEdit.batch_id 
              ? {
                  ...batch,
                  date: editForm.date,
                  batch_actual_stock: editForm.quantity,
                  lane: editForm.lane,
                  slot: editForm.slot
                }
              : batch
          )
        );
        
        closeEditModal();
      } else {
        toast.error('Failed to update batch. Please try again.');
      }
    } catch (error) {
      console.error('Error updating batch:', error);
      toast.error('Failed to update batch. Please try again.');
    } finally {
      setIsEditing(false);
    }
  };

  return (
    <div className="mt-5 h-full">
      <Toaster position="top-right" />
      <Card extra={"w-full h-full p-4"}>
        {/* Header with back button */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-200 dark:bg-navy-700 dark:text-gray-300 dark:hover:bg-navy-600"
            >
              <MdArrowBack className="h-5 w-5" />
              Back
            </button>
            <div>
              <h4 className="text-xl font-bold text-navy-700 dark:text-white">
                {productType} Batches
              </h4>
              <p className="mt-1 text-sm text-gray-600">
                Detailed view of all batches for this product type
              </p>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="mb-8 grid grid-cols-1 gap-6 sm:grid-cols-2">
          <Card extra="p-4">
            <div className="flex items-center gap-4">
              <div className="rounded-full bg-blue-50 p-3 dark:bg-navy-700">
                <MdInventory className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Quantity</p>
                <p className="text-xl font-bold text-navy-700 dark:text-white">{totalQuantity} boxes</p>
              </div>
            </div>
          </Card>

          <Card extra="p-4">
            <div className="flex items-center gap-4">
              <div className="rounded-full bg-orange-50 p-3 dark:bg-navy-700">
                <MdCategory className="h-6 w-6 text-orange-600 dark:text-orange-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Batches</p>
                <p className="text-xl font-bold text-navy-700 dark:text-white">{filteredData.length}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Filters Section */}
        <div className="mb-8 rounded-xl border border-gray-200 bg-white p-4 dark:border-navy-700 dark:bg-navy-800">
          <div className="mb-4 flex items-center gap-2">
            <MdFilterList className="h-5 w-5 text-gray-500" />
            <h3 className="text-lg font-semibold text-gray-700 dark:text-white">Filters</h3>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <label className="mb-2 block text-sm font-medium text-gray-600 dark:text-gray-400">Date Range</label>
              <div className="flex gap-2">
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => setFilters({...filters, startDate: e.target.value})}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700"
                />
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => setFilters({...filters, endDate: e.target.value})}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700"
                />
              </div>
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-600 dark:text-gray-400">Quantity</label>
              <input
                type="number"
                value={filters.quantity}
                onChange={(e) => setFilters({...filters, quantity: e.target.value})}
                placeholder="Filter by quantity"
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700"
              />
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <button
              onClick={() => setFilters({startDate: '', endDate: '', quantity: ''})}
              className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-200 dark:bg-navy-700 dark:text-gray-300 dark:hover:bg-navy-600"
            >
              Clear Filters
            </button>
          </div>
        </div>

        {/* Table header and search */}
        <div className="mb-8 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex h-10 items-center rounded-full bg-white px-4 dark:bg-navy-900">
              <span className="text-xl">
                <MdSearch className="h-5 w-5 text-gray-400" />
              </span>
              <input
                type="text"
                placeholder="Search across all fields..."
                className="ml-2 w-full bg-transparent text-sm font-medium text-gray-600 outline-none dark:text-white"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>

        {loading ? (
          <Loader />
        ) : error ? (
          <div className="flex h-[400px] items-center justify-center">
            <div className="text-center">
              <div className="mb-4 text-5xl text-red-500">
                <MdError className="mx-auto h-16 w-16" />
              </div>
              <p className="text-xl font-semibold text-gray-700 dark:text-white">
                Oops! Something went wrong
              </p>
              <p className="mt-2 text-gray-500 dark:text-gray-400">{error}</p>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  {TABLE_HEAD.map((head, index) => (
                    <th
                      key={index}
                      className="border-b border-gray-200 pb-[10px] pr-4 text-start dark:!border-navy-700"
                    >
                      <p className="text-sm font-bold tracking-wide text-gray-800 dark:text-white">
                        {head}
                      </p>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((item) => (
                  <tr key={item.id}>
                    <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                      <p className="text-sm font-bold text-navy-700 dark:text-white">
                        {item.batch_id}
                      </p>
                    </td>
                    <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                      <p className="text-sm font-bold text-navy-700 dark:text-white">
                        {new Date(item.date).toLocaleDateString()}
                      </p>
                    </td>
                    <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                      <p className="text-sm font-bold text-navy-700 dark:text-white">
                        {item.julian_date || '-'}
                      </p>
                    </td>
                    <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                      <p className="text-sm font-bold text-navy-700 dark:text-white">
                        {item.rcn_type || '-'}
                      </p>
                    </td>
                    <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                      <p className="text-sm font-bold text-navy-700 dark:text-white">
                        {item.batch_actual_stock}
                      </p>
                    </td>
                    <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                      <p className="text-sm font-bold text-navy-700 dark:text-white">
                        {item.lane || `Lane ${item.zone}`}, Slot {item.slot || item.rack}
                      </p>
                    </td>
                    <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEdit(item)}
                          className="rounded-lg p-2 transition-all duration-200 bg-blue-50 text-blue-600 hover:bg-blue-100 dark:bg-navy-700 dark:text-blue-400 dark:hover:bg-navy-600"
                          title="Edit batch"
                        >
                          <MdEdit className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => openDeleteConfirm(item)}
                          className="rounded-lg p-2 transition-all duration-200 bg-red-50 text-red-600 hover:bg-red-100 dark:bg-navy-700 dark:text-red-400 dark:hover:bg-navy-600"
                          title="Delete batch"
                        >
                          <MdDelete className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        <div className="mt-6 flex items-center justify-between">
          <p className="text-sm font-medium text-navy-700 dark:text-white">
            Showing {paginatedData.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0} to {Math.min(currentPage * itemsPerPage, filteredData.length)} of {filteredData.length} entries
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className={`linear rounded-md px-3 py-1 text-sm font-medium transition duration-200 ${
                currentPage === 1
                  ? "cursor-not-allowed bg-gray-300 text-gray-500"
                  : "bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700"
              }`}
            >
              First
            </button>
            <button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
              className={`linear rounded-md px-3 py-1 text-sm font-medium transition duration-200 ${
                currentPage === 1
                  ? "cursor-not-allowed bg-gray-300 text-gray-500"
                  : "bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700"
              }`}
            >
              Previous
            </button>
            
            {/* Page number buttons */}
            {[...Array(Math.min(5, totalPages))].map((_, index) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = index + 1;
              } else if (currentPage <= 3) {
                pageNum = index + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + index;
              } else {
                pageNum = currentPage - 2 + index;
              }
              
              return (
                <button
                  key={index}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`linear rounded-md px-3 py-1 text-sm font-medium transition duration-200 ${
                    currentPage === pageNum
                      ? "bg-brand-700 text-white"
                      : "bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700"
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
            
            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages || totalPages === 0}
              className={`linear rounded-md px-3 py-1 text-sm font-medium transition duration-200 ${
                currentPage === totalPages || totalPages === 0
                  ? "cursor-not-allowed bg-gray-300 text-gray-500"
                  : "bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700"
              }`}
            >
              Next
            </button>
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages || totalPages === 0}
              className={`linear rounded-md px-3 py-1 text-sm font-medium transition duration-200 ${
                currentPage === totalPages || totalPages === 0
                  ? "cursor-not-allowed bg-gray-300 text-gray-500"
                  : "bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700"
              }`}
            >
              Last
            </button>
          </div>
        </div>
      </Card>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl dark:bg-navy-800">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900">
                  <MdWarning className="h-6 w-6 text-red-600 dark:text-red-400" />
                </div>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Delete Batch
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Are you sure you want to delete this batch?
                </p>
              </div>
            </div>
            
            {batchToDelete && (
              <div className="mb-4 p-3 bg-gray-50 rounded-lg dark:bg-navy-700">
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  <span className="font-medium">Batch ID:</span> {batchToDelete.batch_id}
                </p>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  <span className="font-medium">Quantity:</span> {batchToDelete.batch_actual_stock} boxes
                </p>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  <span className="font-medium">Date:</span> {new Date(batchToDelete.date).toLocaleDateString()}
                </p>
              </div>
            )}
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Deletion Note <span className="text-red-500">*</span>
              </label>
              <textarea
                value={deleteNote}
                onChange={(e) => setDeleteNote(e.target.value)}
                rows="3"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                placeholder="Enter a note explaining why this batch is being deleted..."
              />
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={closeDeleteConfirm}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 dark:bg-navy-700 dark:text-gray-300 dark:border-navy-600 dark:hover:bg-navy-600"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={!deleteNote.trim() || isDeleting}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {isDeleting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Deleting...</span>
                  </>
                ) : (
                  <span>Delete</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && batchToEdit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 shadow-xl dark:bg-navy-800 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900">
                    <MdEdit className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                  </div>
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    Edit Batch
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Update batch information
                  </p>
                </div>
              </div>
              <button
                onClick={closeEditModal}
                className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
              >
                <MdClose className="h-6 w-6" />
              </button>
            </div>

            {/* Current Batch Info */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg dark:bg-navy-700">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Current Batch Information</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-600 dark:text-gray-400">Batch ID:</span>
                  <p className="text-gray-900 dark:text-white">{batchToEdit.batch_id}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-600 dark:text-gray-400">Product Type:</span>
                  <p className="text-gray-900 dark:text-white">{batchToEdit.product_type}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-600 dark:text-gray-400">Current Date:</span>
                  <p className="text-gray-900 dark:text-white">{new Date(batchToEdit.date).toLocaleDateString()}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-600 dark:text-gray-400">Current Quantity:</span>
                  <p className="text-gray-900 dark:text-white">{batchToEdit.batch_actual_stock} boxes</p>
                </div>
                <div>
                  <span className="font-medium text-gray-600 dark:text-gray-400">Current Location:</span>
                  <p className="text-gray-900 dark:text-white">
                    {batchToEdit.lane || `Lane ${batchToEdit.zone}`}, Slot {batchToEdit.slot || batchToEdit.rack}
                  </p>
                </div>
              </div>
            </div>

            {/* Edit Form */}
            <form onSubmit={handleEditSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    name="date"
                    value={editForm.date}
                    onChange={handleEditFormChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Quantity <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="quantity"
                    value={editForm.quantity}
                    onChange={handleEditFormChange}
                    min="1"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                    placeholder="Enter quantity"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
                    Lane (Read Only)
                  </label>
                  <input
                    type="text"
                    name="lane"
                    value={editForm.lane}
                    readOnly
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 text-gray-500 cursor-not-allowed dark:border-navy-600 dark:bg-navy-600 dark:text-gray-400"
                    placeholder="Lane number"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
                    Slot (Read Only)
                  </label>
                  <input
                    type="text"
                    name="slot"
                    value={editForm.slot}
                    readOnly
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 text-gray-500 cursor-not-allowed dark:border-navy-600 dark:bg-navy-600 dark:text-gray-400"
                    placeholder="Slot number"
                  />
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-navy-600">
                <button
                  type="button"
                  onClick={closeEditModal}
                  disabled={isEditing}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 dark:bg-navy-700 dark:text-gray-300 dark:border-navy-600 dark:hover:bg-navy-600"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isEditing}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 flex items-center space-x-2"
                >
                  {isEditing ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Updating...</span>
                    </>
                  ) : (
                    <>
                      <MdSave className="h-4 w-4" />
                      <span>Update Batch</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductTypeDetail; 